import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import styled from 'styled-components';
import { Avatar } from '@producer/ui-kit';

export interface MentionItem {
  id: string;
  label: string;
  avatar?: string;
  initials?: string;
}

interface MentionListProps {
  items: MentionItem[];
  command: (item: MentionItem) => void;
}

export interface MentionListRef {
  onKeyDown: (event: { event: KeyboardEvent }) => boolean;
}

export const MentionList = forwardRef<MentionListRef, MentionListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index: number) => {
    const item = props.items[index];

    if (item) {
      props.command(item);
    }
  };

  const upHandler = () => {
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => setSelectedIndex(0), [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }

      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  return (
    <Container>
      {props.items.length ? (
        props.items.map((item, index) => (
          <Item
            key={item.id}
            data-selected={index === selectedIndex}
            onClick={() => selectItem(index)}
          >
            <Avatar size="xsmall" initials={item.initials} src={item.avatar} />
            <span>{item.label}</span>
          </Item>
        ))
      ) : (
        <EmptyItem>No members found</EmptyItem>
      )}
    </Container>
  );
});

MentionList.displayName = 'MentionList';

const Container = styled.div`
  background: var(--color-grayscale-charleston);
  border: 1px solid var(--color-grayscale-trout);
  border-radius: 0.8rem;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0px 10px 20px rgba(0, 0, 0, 0.1);
  overflow: auto;
  padding: 0.4rem;
  max-height: 20rem;
`;

const Item = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
  padding: 0.8rem;
  border-radius: 0.4rem;
  font-size: 1.4rem;
  color: var(--color-grayscale-white);
  cursor: pointer;

  &[data-selected='true'] {
    background: var(--color-grayscale-trout);
  }

  &:hover {
    background: var(--color-grayscale-trout);
  }
`;

const EmptyItem = styled.div`
  padding: 0.8rem;
  font-size: 1.4rem;
  color: var(--color-grayscale-manatee);
  text-align: center;
`;
